import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const { login, register } = useAuth();
  const [tab, setTab] = useState(0); // 0 = login, 1 = register
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  // Registration form state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setError('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(loginData.email, loginData.password);

      if (!result.success) {
        setError(result.error || 'Login failed');
      }
      // If successful, AuthContext will update and App will re-render
    } catch (err) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (registerData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const result = await register({
        email: registerData.email,
        password: registerData.password,
        first_name: registerData.firstName,
        last_name: registerData.lastName,
        tenant_id: process.env.REACT_APP_DEFAULT_TENANT_ID || 'mbco',
        role: 'resource_manager', // Default role
      });

      if (!result.success) {
        setError(result.error || 'Registration failed');
      }
      // If successful, AuthContext will update and App will re-render
    } catch (err) {
      setError(err.message || 'An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#121212',
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: '100%',
          bgcolor: '#2D2D2D',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              textAlign: 'center',
              color: '#BB86FC',
              fontWeight: 600,
            }}
          >
            Concierge
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              textAlign: 'center',
              color: '#999',
            }}
          >
            Concierge Talent Management
          </Typography>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{
              mb: 3,
              '& .MuiTab-root': {
                color: '#999',
                '&.Mui-selected': {
                  color: '#BB86FC',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#BB86FC',
              },
            }}
          >
            <Tab label="Login" sx={{ flex: 1 }} />
            <Tab label="Register" sx={{ flex: 1 }} />
          </Tabs>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          {tab === 0 && (
            <form onSubmit={handleLoginSubmit}>
              <TextField
                label="Email"
                type="email"
                value={loginData.email}
                onChange={(e) =>
                  setLoginData({ ...loginData, email: e.target.value })
                }
                required
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#3A3A3A',
                    '& fieldset': { borderColor: '#666' },
                    '&:hover fieldset': { borderColor: '#BB86FC' },
                    '&.Mui-focused fieldset': { borderColor: '#BB86FC' },
                  },
                  '& .MuiInputLabel-root': { color: '#BBB' },
                  '& .MuiOutlinedInput-input': { color: '#FFF' },
                }}
              />

              <TextField
                label="Password"
                type="password"
                value={loginData.password}
                onChange={(e) =>
                  setLoginData({ ...loginData, password: e.target.value })
                }
                required
                fullWidth
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#3A3A3A',
                    '& fieldset': { borderColor: '#666' },
                    '&:hover fieldset': { borderColor: '#BB86FC' },
                    '&.Mui-focused fieldset': { borderColor: '#BB86FC' },
                  },
                  '& .MuiInputLabel-root': { color: '#BBB' },
                  '& .MuiOutlinedInput-input': { color: '#FFF' },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  bgcolor: '#BB86FC',
                  '&:hover': { bgcolor: '#9A67EA' },
                  height: 48,
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
            </form>
          )}

          {/* Register Form */}
          {tab === 1 && (
            <form onSubmit={handleRegisterSubmit}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="First Name"
                  value={registerData.firstName}
                  onChange={(e) =>
                    setRegisterData({
                      ...registerData,
                      firstName: e.target.value,
                    })
                  }
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#3A3A3A',
                      '& fieldset': { borderColor: '#666' },
                      '&:hover fieldset': { borderColor: '#BB86FC' },
                      '&.Mui-focused fieldset': { borderColor: '#BB86FC' },
                    },
                    '& .MuiInputLabel-root': { color: '#BBB' },
                    '& .MuiOutlinedInput-input': { color: '#FFF' },
                  }}
                />

                <TextField
                  label="Last Name"
                  value={registerData.lastName}
                  onChange={(e) =>
                    setRegisterData({
                      ...registerData,
                      lastName: e.target.value,
                    })
                  }
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#3A3A3A',
                      '& fieldset': { borderColor: '#666' },
                      '&:hover fieldset': { borderColor: '#BB86FC' },
                      '&.Mui-focused fieldset': { borderColor: '#BB86FC' },
                    },
                    '& .MuiInputLabel-root': { color: '#BBB' },
                    '& .MuiOutlinedInput-input': { color: '#FFF' },
                  }}
                />
              </Box>

              <TextField
                label="Email"
                type="email"
                value={registerData.email}
                onChange={(e) =>
                  setRegisterData({ ...registerData, email: e.target.value })
                }
                required
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#3A3A3A',
                    '& fieldset': { borderColor: '#666' },
                    '&:hover fieldset': { borderColor: '#BB86FC' },
                    '&.Mui-focused fieldset': { borderColor: '#BB86FC' },
                  },
                  '& .MuiInputLabel-root': { color: '#BBB' },
                  '& .MuiOutlinedInput-input': { color: '#FFF' },
                }}
              />

              <TextField
                label="Password"
                type="password"
                value={registerData.password}
                onChange={(e) =>
                  setRegisterData({
                    ...registerData,
                    password: e.target.value,
                  })
                }
                required
                fullWidth
                helperText="Minimum 8 characters"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#3A3A3A',
                    '& fieldset': { borderColor: '#666' },
                    '&:hover fieldset': { borderColor: '#BB86FC' },
                    '&.Mui-focused fieldset': { borderColor: '#BB86FC' },
                  },
                  '& .MuiInputLabel-root': { color: '#BBB' },
                  '& .MuiOutlinedInput-input': { color: '#FFF' },
                  '& .MuiFormHelperText-root': { color: '#999' },
                }}
              />

              <TextField
                label="Confirm Password"
                type="password"
                value={registerData.confirmPassword}
                onChange={(e) =>
                  setRegisterData({
                    ...registerData,
                    confirmPassword: e.target.value,
                  })
                }
                required
                fullWidth
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#3A3A3A',
                    '& fieldset': { borderColor: '#666' },
                    '&:hover fieldset': { borderColor: '#BB86FC' },
                    '&.Mui-focused fieldset': { borderColor: '#BB86FC' },
                  },
                  '& .MuiInputLabel-root': { color: '#BBB' },
                  '& .MuiOutlinedInput-input': { color: '#FFF' },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  bgcolor: '#BB86FC',
                  '&:hover': { bgcolor: '#9A67EA' },
                  height: 48,
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Register'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
